<template>
  <div class="alert-container-wrapper">
    <div
      v-for="position in positions"
      :key="position"
      :class="['alert-position', position]"
    >
      <transition-group name="alert-item" tag="div">
        <div
          v-for="alert in getAlertsByPosition(position)"
          :key="alert.id"
          :class="['alert-item', alert.type]"
        >
          <span class="alert-message">{{ alert.message }}</span>
          <button @click="removeNotification(alert.id)" class="alert-close">
            &times;
          </button>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import { AlertBus } from '@/plugins/AlertBus.js';

export default {
  name: 'AlertContainer',
  data() {
    return {
      notifications: [],
      nextId: 0,
      positions: [
        'top-left',
        'top-center',
        'top-right',
        'bottom-left',
        'bottom-center',
        'bottom-right',
      ],
    };
  },
  methods: {
    /**
     * Filtra os alertas que pertencem a uma posição específica.
     */
    getAlertsByPosition(position) {
      return this.notifications.filter((n) => n.position === position);
    },
    
    /**
     * Adiciona uma nova notificação à lista.
     */
    addNotification(options) {
      const id = this.nextId++;
      
      // Valores padrão
      const defaults = {
        id: id,
        message: 'Ocorreu um evento.',
        type: 'info', // info, success, alerta, erro
        position: 'top-right', // 6 posições
        duration: 4000, // 4 segundos
      };
      
      const alert = { ...defaults, ...options };
      
      this.notifications.push(alert);
      
      // Define um timer para remover o alerta
      setTimeout(() => {
        this.removeNotification(id);
      }, alert.duration);
    },
    
    /**
     * Remove uma notificação pelo seu ID.
     */
    removeNotification(id) {
      this.notifications = this.notifications.filter((n) => n.id !== id);
    },
  },
  created() {
    // Começa a ouvir por eventos 'show-alert'
    AlertBus.$on('show-alert', this.addNotification);
  },
  beforeDestroy() {
    // Para de ouvir para evitar leaks de memória
    AlertBus.$off('show-alert', this.addNotification);
  },
};
</script>

<style scoped>
.alert-container-wrapper {
  position: relative;
}

.alert-position {
  position: fixed;
  display: flex;
  flex-direction: column;
  z-index: 9999;
  pointer-events: none;
}

/* --- Posições (Padrão para Telas Grandes / PC) --- */
.top-right {
  top: 20px;
  right: 20px;
  align-items: flex-end;
}
.top-left {
  top: 20px;
  left: 20px;
  align-items: flex-start;
}
.top-center {
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  align-items: center;
}
.bottom-right {
  bottom: 20px;
  right: 20px;
  align-items: flex-end;
}
.bottom-left {
  bottom: 20px;
  left: 20px;
  align-items: flex-start;
}
.bottom-center {
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  align-items: center;
}

/* --- Item de Alerta Individual --- */
.alert-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  width: 320px;
  max-width: 90vw;
  padding: 14px 20px;
  margin-bottom: 10px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(0, 0, 0, 0.1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica,
  Arial, sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  pointer-events: all;
  color: #fff;
}

.alert-message {
  line-height: 1.4;
  flex: 1;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.alert-close {
  background: none;
  border: none;
  color: inherit;
  opacity: 0.7;
  cursor: pointer;
  font-size: 1.4rem;
  font-weight: bold;
  padding: 0 0 0 10px;
  transition: opacity 0.2s;
  align-self: flex-start;
}
.alert-close:hover {
  opacity: 1;
}

/* --- Tipos de Alerta (Cores) --- */
.alert-item.success {
  background-color: #4caf50; /* Verde */
}
.alert-item.erro {
  background-color: #f44336; /* Vermelho */
}
.alert-item.alerta {
  background-color: #ff9800; /* Laranja */
  color: #000;
}
.alert-item.info {
  background-color: #2196f3; /* Azul */
}

/*
  ==============================================================
   ANIMAÇÃO MELHORADA (CORREÇÃO)
  ==============================================================
*/

/* 1. Animação de ENTRADA (slide up + fade in) */
.alert-item-enter-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.alert-item-enter {
  opacity: 0;
  transform: translateY(15px);
}

/* 2. Animação de SAÍDA (Apenas fade out) */
.alert-item-leave-active {
  /* position: absolute é crucial para o -move funcionar */
  position: absolute;
  /* Animamos apenas a opacidade ao sair. Rápido e limpo. */
  transition: opacity 0.3s ease-in;
}
.alert-item-leave-to {
  opacity: 0;
  /* Removemos o transform: translateY() daqui, que causava o "movimento estranho" */
}

/* 3. Animação de MOVIMENTO (Itens restantes) */
.alert-item-move {
  /* Esta é a classe que o Vue aplica aos itens que sobram
     para eles deslizarem e ocuparem o espaço.
     Isso remove o "tranco" (travada). */
  transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/*
  ==============================================================
   REGRAS DE RESPONSIVIDADE (MOBILE)
  ==============================================================
*/
@media (max-width: 600px) {
  .top-left,
  .top-right {
    top: 20px;
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    align-items: center;
  }
  
  .bottom-left,
  .bottom-right {
    bottom: 20px;
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    align-items: center;
  }
}
</style>